import React, { useState, useEffect, useMemo } from 'react';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';

export interface PresetChange {
  id: number;
  shift: string;
  preset: string;
  old_value: string;
  new_value: string;
  timestamp?: string;
}

interface PresetMarkersProps {
  machineSerialNo?: string;
  enabled: boolean;
  onPresetDataChange: (presetData: PresetChange[]) => void;
}

interface PresetToggleProps {
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  presetCount: number;
  className?: string;
}

// Hook for preset data management
export const usePresetMarkers = (machineSerialNo?: string, enabled: boolean = false, shifts?: string[]) => {
  const { dateRange } = useSAPDataStore();
  const { token } = useUnifiedAuth();
  const [presetData, setPresetData] = useState<PresetChange[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch preset changes data
  const fetchPresetData = async () => {
    if (!machineSerialNo || !dateRange.start || !dateRange.end || !enabled || !token) {
      setPresetData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, []);
      const response = await ChartDataService.fetchPresetDetails(params, token);

      if (response.success && response.data) {
        const responseData = response.data as any;
        // Backend may return { results: [...] } or results directly as array
        const results = Array.isArray(responseData) ? responseData : (responseData.results || []);
        
        // Response format: [{ timestamp, preset, old_value, new_value, shift }, ...]
        const presetChanges: PresetChange[] = results.map((item: any) => ({
          id: 0,
          timestamp: item.timestamp,
          preset: item.preset,
          old_value: item.old_value,
          new_value: item.new_value,
          shift: item.shift
        }));
        
        setPresetData(presetChanges);
      } else {
        setPresetData([]);
      }
    } catch (error) {
      console.error('Failed to fetch preset data:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch preset data');
      setPresetData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch preset data when enabled or dependencies change
  useEffect(() => {
    fetchPresetData();
  }, [machineSerialNo, dateRange.start, dateRange.end, enabled]);

  // PC.presets_detail ignores shift_name, so the markers filter the shifts themselves.
  // Its `shift` is shift.name, the same names the shift selector lists. Filtering the
  // fetched data here means a shift change does not refetch.
  const shiftFilteredPresetData = useMemo(
    () => (shifts && shifts.length > 0
      ? presetData.filter(change => shifts.includes(change.shift))
      : presetData),
    [presetData, shifts]
  );

  return {
    presetData: shiftFilteredPresetData,
    loading,
    error,
    refetch: fetchPresetData
  };
};

// Generate chart options for preset markers
export const generatePresetPlotLines = (presetData: PresetChange[], enabled: boolean) => {
  if (!enabled || presetData.length === 0) return [];

  const plotLines = presetData.map((preset, index) => {
    // Handle timestamp - may already be ISO format or "2015-01-21 09:46:59" format
    const timestamp = preset.timestamp?.includes('T') ? preset.timestamp : preset.timestamp?.replace(' ', 'T') || '';
    const parsedTime = new Date(timestamp).getTime();
    
    return {
      value: parsedTime,
      color: '#ff6b6b',
      dashStyle: 'ShortDash' as const,
      width: 2,
      label: {
        text: `P${index + 1}`,
        style: {
          color: '#ff6b6b',
          fontSize: '10px',
          fontWeight: 'bold'
        },
        rotation: 0,
        textAlign: 'center' as const
      },
      zIndex: 1000,
      events: {
        mouseover: function(this: any) {
          const chart = this.axis.chart;
          const currentPreset = presetData[index];
          
          if (chart.presetTooltip) {
            chart.presetTooltip.destroy();
          }

          chart.presetTooltip = chart.renderer.label(
            `<b>Preset Change ${index + 1}</b><br/>` +
            `Time: ${new Date(currentPreset.timestamp || '').toLocaleString()}<br/>` +
            `Preset: ${currentPreset.preset}<br/>` +
            `Change: ${currentPreset.old_value} → ${currentPreset.new_value}<br/>` +
            `Shift: ${currentPreset.shift}`,
            this.axis.toPixels(this.options.value) + 10,
            50,
            'rect',
            0,
            0,
            true
          )
          .css({
            color: '#333',
            fontSize: '11px',
            padding: '8px'
          })
          .attr({
            fill: 'rgba(255, 255, 255, 0.95)',
            stroke: '#ff6b6b',
            'stroke-width': 1,
            r: 4,
            zIndex: 2000
          })
          .shadow(true)
          .add();
        },
        mouseout: function(this: any) {
          const chart = this.axis.chart;
          if (chart.presetTooltip) {
            chart.presetTooltip.destroy();
            chart.presetTooltip = null;
          }
        }
      }
    };
  }).filter(line => line.value > 0);
  
  return plotLines;
};

// Generate chart cleanup events
export const generatePresetChartEvents = () => ({
  // Clean up any lingering preset tooltips when chart is destroyed or redrawn
  redraw: function(this: any) {
    if (this.presetTooltip) {
      this.presetTooltip.destroy();
      this.presetTooltip = null;
    }
  }
});

// Enhanced tooltip formatter that includes preset information
export const enhanceTooltipWithPresets = (
  originalFormatter: (this: any) => string,
  presetData: PresetChange[],
  enabled: boolean
) => {
  return function(this: any) {
    let tooltip = originalFormatter.call(this);
    
    // Add preset change info if there's one on this date
    if (enabled && presetData.length > 0) {
      const pointDate = new Date(this.x);
      const presetChangesOnDate = presetData.filter(preset => {
        if (!preset.timestamp) return false;
        const presetDate = new Date(preset.timestamp);
        return presetDate.toDateString() === pointDate.toDateString();
      });
      
      if (presetChangesOnDate.length > 0) {
        tooltip += '<br/><b>Preset Changes:</b><br/>';
        presetChangesOnDate.forEach(preset => {
          tooltip += `• ${preset.preset}: ${preset.old_value} → ${preset.new_value}<br/>`;
        });
      }
    }
    
    return tooltip;
  };
};

// Toggle component for preset markers
export const PresetToggle: React.FC<PresetToggleProps> = ({ 
  enabled, 
  onToggle, 
  presetCount: _presetCount, 
  className = "" 
}) => {
  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle(!enabled);
  };

  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex flex-row-reverse sm:flex-row items-center gap-1.5 sm:gap-2">
        <label className="text-xs sm:text-sm font-medium text-gray-700 dark:text-white select-none">
          Show Preset Changes
        </label>
        <button
          type="button"
          onClick={handleToggle}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
            dark:focus:ring-offset-gray-800
            ${enabled 
              ? 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700' 
              : 'bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500'
            }
          `}
          role="switch"
          aria-checked={enabled}
          aria-label="Toggle preset change markers"
        >
          <span
            className={`
              inline-block h-4 w-4 transform rounded-full transition-transform pointer-events-none
              ${enabled 
                ? 'translate-x-6 bg-white dark:bg-white' 
                : 'translate-x-1 bg-white dark:bg-gray-200'
              }
            `}
          />
        </button>
      </div>
    </div>
  );
};

// Main component that handles preset markers functionality
const PresetMarkers: React.FC<PresetMarkersProps> = ({ 
  machineSerialNo, 
  enabled, 
  onPresetDataChange
}) => {
  const { presetData } = usePresetMarkers(machineSerialNo, enabled);

  // Update parent component with preset data changes
  useEffect(() => {
    onPresetDataChange(presetData);
  }, [presetData, onPresetDataChange]);

  return null; // This is a logic-only component
};

export default PresetMarkers;
